import type { MutableRefObject } from 'react';
import { useLayoutEffect, useState } from 'react';

const useIsMoreScroll = (
  scrollableRef: MutableRefObject<HTMLDivElement | null>,
  { isDisabled, height }: { isDisabled?: boolean; height: number } = {
    height: 80,
  },
) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const [isMoreScrollTop, setIsMoreScrollTop] = useState(0);
  const [isMoreScrollBottom, setIsMoreScrollBottom] = useState(0);

  useLayoutEffect(() => {
    const sidebar = scrollableRef.current;
    if (!sidebar || isDisabled) {
      return;
    }

    let timeout: NodeJS.Timeout | undefined;
    let rafId: number | null = null;

    const updateScrollState = () => {
      setIsMoreScrollTop(Math.min(height, sidebar.scrollTop));
      setIsMoreScrollBottom(
        Math.min(
          height,
          Math.abs(
            sidebar.scrollHeight - sidebar.scrollTop - sidebar.clientHeight,
          ),
        ),
      );

      if (timeout) {
        clearTimeout(timeout);
      }
      setIsScrolling(true);
      timeout = setTimeout(() => {
        setIsScrolling(false);
      }, 500);
    };

    // Throttle with requestAnimationFrame for better performance
    const handleScroll = () => {
      if (rafId) {
        return;
      }

      rafId = requestAnimationFrame(() => {
        updateScrollState();
        rafId = null;
      });
    };

    updateScrollState();
    const mutationObserver = new MutationObserver(handleScroll);
    mutationObserver.observe(sidebar, {
      childList: true,
      subtree: true,
    });

    sidebar.addEventListener('scroll', handleScroll);
    return () => {
      sidebar.removeEventListener('scroll', handleScroll);
      mutationObserver.disconnect();
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [height, isDisabled, scrollableRef]);

  return {
    isScrolling,
    isMoreScrollTop,
    isMoreScrollBottom,
  };
};

export default useIsMoreScroll;
